import React from 'react';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';

const EDUCATION = [
  {
    abbr: 'MS',
    degree: 'Master of Science in Artificial Intelligence',
    school: 'Long Island University Brooklyn',
    location: 'Brooklyn, NY',
    period: 'Jan 2025 – Present',
    statusLabel: 'In Progress',
    statusColor: '#F5A623',
    accentColor: '#00D4FF',
    focus: 'AI Agents · NLP · Deep Learning · ML Systems',
    award: null,
  },
  {
    abbr: 'BTech',
    degree: 'Bachelor of Technology - Computer Science',
    school: 'Centurion University of Technology and Management',
    location: 'Vizianagaram, India',
    period: 'Sep 2020 – May 2024',
    statusLabel: 'Completed',
    statusColor: '#39FF14',
    accentColor: '#39FF14',
    focus: 'Data Structures · Machine Learning · Software Engineering · Databases',
    award: 'Academic Excellence Award',
  },
];

const EducationSection = () => (
  <section id="education" className="py-20 relative z-10">
    <div className="container mx-auto px-6 lg:px-16">
      <motion.span
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="section-label block mb-4"
      >
        // MS IN AI, IN PROGRESS
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-12"
      >
        Education
      </motion.h2>

      <div className="space-y-6">
        {EDUCATION.map((edu, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.12, duration: 0.5 }}
            className="flex gap-0 overflow-hidden rounded-xl"
            style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            {/* Left accent stripe + abbr */}
            <div
              className="flex-shrink-0 w-28 flex flex-col items-center justify-center py-6 gap-1.5 overflow-hidden"
              style={{ background: `${edu.accentColor}08`, borderRight: `2px solid ${edu.accentColor}30` }}
            >
              <span
                className="font-display font-extrabold leading-none text-center px-2"
                style={{
                  color: edu.accentColor,
                  fontSize: edu.abbr.length > 2 ? '1.1rem' : '1.5rem',
                }}
              >
                {edu.abbr}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 px-6 py-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-bold text-[#F0F4FF] text-base leading-snug break-words">
                    {edu.degree}
                  </h3>
                  <p className="font-mono text-sm mt-0.5 break-words" style={{ color: edu.accentColor }}>
                    {edu.school}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  {/* Status */}
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] font-semibold tracking-wide"
                    style={{
                      color: edu.statusColor,
                      background: `${edu.statusColor}10`,
                      border: `1px solid ${edu.statusColor}25`,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: edu.statusColor }} />
                    {edu.statusLabel}
                  </span>

                  {/* Award */}
                  {edu.award && (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] font-semibold"
                      style={{
                        color: '#F5A623',
                        background: 'rgba(245,166,35,0.08)',
                        border: '1px solid rgba(245,166,35,0.2)',
                      }}
                    >
                      <Award size={10} />
                      {edu.award}
                    </span>
                  )}
                </div>
              </div>

              {/* Location + period */}
              <p className="font-mono text-xs text-[#4A5A6A] mb-3">
                {edu.location} &nbsp;·&nbsp; {edu.period}
              </p>

              {/* Focus */}
              <p className="font-mono text-xs text-[#5A6878] leading-relaxed">
                {edu.focus}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default EducationSection;
